import { useEffect, useRef, useState } from 'react';
import axios from '../services/api';
import {
  BanknotesIcon,
  ArrowUpTrayIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { MessageCircle } from 'lucide-react';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useFinanceLockStatus from '../hooks/useFinanceLockStatus';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';

const RUPEE_SYMBOL = '\u20B9';

function formatDateValue(dateValue, dateFormat = 'dd MMM yyyy') {
  if (!dateValue) return '-';

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return String(dateValue);

  return format(parsed, dateFormat);
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `${RUPEE_SYMBOL}${amount.toFixed(2)}`;
}

function openBlobInNewTab(blob) {
  const blobUrl = URL.createObjectURL(blob);
  const newWindow = window.open(blobUrl, '_blank');

  if (!newWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Please allow pop-ups to open the PDF.');
  }

  try {
    newWindow.opener = null;
  } catch (_error) {
    // Ignore browser restrictions once the PDF tab is already open.
  }

  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 60000);
}

function getPatientName(record) {
  return record?.Patient?.name || 'Unknown Patient';
}

function getPatientPhone(record) {
  return record?.Patient?.phone || '-';
}

function getPatientCode(record) {
  return record?.Patient?.patientId || '-';
}

function getPatientDatabaseId(record) {
  return record?.Patient?.id || record?.patientId || record?.PatientId || null;
}

function getPatientDocumentKey(record) {
  return String(getPatientDatabaseId(record) || getPatientCode(record) || '').trim();
}

function matchesPatientDocument(row, identity) {
  const patient = row?.Patient || {};

  return (
    (identity.id && Number(patient.id) === Number(identity.id)) ||
    (identity.id && Number(row?.patientId) === Number(identity.id)) ||
    (identity.code && String(patient.patientId || '') === String(identity.code))
  );
}

function getRemarkPreview(text) {
  const normalized = String(text || '').trim();
  if (!normalized) return 'No remarks';
  if (normalized.length <= 48) return normalized;
  return `${normalized.slice(0, 48)}...`;
}

function getEmiSummary(record) {
  const schedule = record?.emiSchedule || record?.EmiSchedules || [];
  const pending = schedule.filter((item) => item.paymentStatus !== 'Done' && item.status !== 'Paid');
  const paidCount = schedule.length - pending.length;

  if (schedule.length === 0) {
    return 'No instalments';
  }

  if (pending.length === 0) {
    return `Fully paid | ${schedule.length}/${schedule.length}`;
  }

  const nextDue = pending[0]?.dueDate ? formatDateValue(pending[0].dueDate, 'dd MMM') : 'No date';
  return `${paidCount}/${schedule.length} paid | Next ${nextDue}`;
}

function getReminderStageClasses(stage) {
  if (stage === 'aggressive_followup') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  if (stage === 'soft_followup' || stage === 'before_due') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getPaymentStatusClasses(status) {
  if (status === 'Done' || status === 'Paid') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  return 'border-amber-200 bg-amber-50 text-amber-700';
}

const sectionSurfaceClasses =
  'section-card overflow-hidden border border-slate-200/70 shadow-[0_8px_22px_rgba(15,23,42,0.04)] ring-0';
const nestedPanelClasses =
  'rounded-[20px] border border-slate-200/80 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.045)]';
const rowSurfaceClasses =
  'rounded-[18px] border border-slate-200/80 bg-white shadow-[0_8px_18px_rgba(15,23,42,0.04)]';
const compactMetaCardClasses =
  'rounded-[16px] border border-slate-200/80 bg-white px-3.5 py-2.5 shadow-[0_3px_10px_rgba(15,23,42,0.025)]';
const toggleButtonClasses =
  'inline-flex items-center justify-center gap-1.5 self-start rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100';
const neutralActionChipClasses =
  'inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 shadow-[0_2px_8px_rgba(15,23,42,0.03)] transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60';
const primaryActionChipClasses =
  'inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-60';
const upcomingSectionClasses = `${sectionSurfaceClasses} bg-blue-50/55`;
const completedSectionClasses = `${sectionSurfaceClasses} bg-emerald-50/55`;
const emiSectionClasses = `${sectionSurfaceClasses} bg-violet-50/55`;
const patientHistoryTabs = [
  { key: 'upcoming', label: 'Upcoming Cases' },
  { key: 'emi', label: 'EMI Records' },
  { key: 'completed', label: 'Completed Cases' },
  { key: 'all', label: 'All Patients' },
];

const amendmentSections = [
  { key: 'clinical_update', label: 'General Clinical Update' },
  { key: 'chief_complaint', label: 'Chief Complaint' },
  { key: 'medical_history', label: 'Medical History' },
  { key: 'dental_findings', label: 'Dental / Tooth Finding' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'additional_doctor_notes', label: 'Additional Doctor Notes' },
  { key: 'treatment_plan_information', label: 'Treatment-plan Clinical Information' },
  { key: 'follow_up_information', label: 'Follow-up Information' },
  { key: 'prescription_information', label: 'Prescription Information / Advice' },
];

function revisionValue(snapshot) {
  if (!snapshot) return '';
  const direct = snapshot.addedText ?? snapshot.value ?? snapshot.patientMedicalHistory ?? snapshot.medicalHistory ?? snapshot.doctorNotes ?? snapshot.generalAdvice;
  if (direct !== undefined && direct !== null && String(direct).trim()) return String(direct);
  if (snapshot.issue) {
    try {
      const issue = JSON.parse(snapshot.issue);
      return [...(issue.findings || []), issue.note].filter(Boolean).join(' · ');
    } catch (_error) {
      return String(snapshot.issue);
    }
  }
  const visible = Object.fromEntries(Object.entries(snapshot).filter(([key]) => !['amendmentMode', 'reasonContext'].includes(key)));
  return Object.keys(visible).length ? JSON.stringify(visible) : '';
}

function originalContextLines(context, revisions) {
  const sections = ['chief_complaint', 'medical_history', 'additional_doctor_notes', 'prescription_information'];
  return sections.map((section) => {
    const firstCorrection = revisions.find((revision) => Number(revision.encounterId) === Number(context.encounterId) && revision.section === section && revision.revisionType === 'CLINICAL_INFORMATION_CORRECTED');
    const value = firstCorrection ? revisionValue(firstCorrection.beforeValue) : context.current?.[section];
    const label = amendmentSections.find((item) => item.key === section)?.label || section.replace(/_/g, ' ');
    return value ? `${label}: ${value}` : '';
  }).filter(Boolean);
}

export default function PatientHistory() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchRequestRef = useRef(0);
  const focusedPatientRef = useRef(null);
  const financeStatus = useFinanceLockStatus();
  const canViewClinicalHistory = useAuthStore((state) => state.user?.capabilities?.includes('clinical.view'));
  const canAmendClinicalHistory = useAuthStore((state) => state.user?.capabilities?.includes('clinical.edit'));
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [upcomingNew, setUpcomingNew] = useState([]);
  const [upcomingFollowup, setUpcomingFollowup] = useState([]);
  const [completedCases, setCompletedCases] = useState([]);
  const [emiRecords, setEmiRecords] = useState([]);
  const [remarksDrafts, setRemarksDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingRemarkId, setSavingRemarkId] = useState(null);
  const [emiActionKey, setEmiActionKey] = useState('');
  const [expandedUpcomingRow, setExpandedUpcomingRow] = useState('');
  const [expandedCompletedCase, setExpandedCompletedCase] = useState(null);
  const [expandedEmiRecord, setExpandedEmiRecord] = useState(null);
  const [documentsByPatient, setDocumentsByPatient] = useState({});
  const [documentActionKey, setDocumentActionKey] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [allPatientsPage, setAllPatientsPage] = useState(1);
  const [allPatientsTotal, setAllPatientsTotal] = useState(0);
  const [allPatientsTotalPages, setAllPatientsTotalPages] = useState(1);
  const [allPatientsSearch, setAllPatientsSearch] = useState('');
  const [allPatientsLoading, setAllPatientsLoading] = useState(false);
  const [expandedDirectoryPatient, setExpandedDirectoryPatient] = useState(null);
  const [focusedPatient, setFocusedPatient] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [amendmentContexts, setAmendmentContexts] = useState([]);
  const [amendmentDentalFindings, setAmendmentDentalFindings] = useState([]);
  const [amendmentModal, setAmendmentModal] = useState(null);
  const [amendmentDraft, setAmendmentDraft] = useState({ mode: 'ADD', section: 'clinical_update', value: '', reason: '', dentalEntryId: '' });
  const [savingAmendment, setSavingAmendment] = useState(false);
  const [revisionHistoryOpen, setRevisionHistoryOpen] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('upcoming');
  const [oldRecordUploadKey, setOldRecordUploadKey] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const debouncedAllPatientsSearch = useDebouncedValue(allPatientsSearch, 300);
  const allPatientsLimit = 25;
  const canViewBills =
    !financeStatus.loading &&
    financeStatus.canUnlock &&
    financeStatus.unlocked &&
    !financeStatus.restricted &&
    !financeStatus.error;
  const requestedPatientId = searchParams.get('patientId') || location.state?.patientId || '';

  useEffect(() => {
    fetchUpcoming();
  }, []);

  useEffect(() => {
    const patientId = Number(requestedPatientId);
    if (!Number.isInteger(patientId) || patientId <= 0) {
      setFocusedPatient(null);
      return;
    }

    let cancelled = false;
    const openRequestedPatient = async () => {
      try {
        const response = await axios.get(`/patients/${encodeURIComponent(patientId)}`);
        if (cancelled || !response.data?.id) return;
        setFocusedPatient(response.data);
        setActiveHistoryTab('all');
        setExpandedDirectoryPatient(response.data.id);
      } catch (error) {
        if (!cancelled && error.response?.status !== 404) {
          toast.error(error.response?.data?.error || 'Could not open the selected patient.');
        }
      }
    };

    openRequestedPatient();
    return () => {
      cancelled = true;
    };
  }, [requestedPatientId]);

  useEffect(() => {
    if (!focusedPatient?.id || !canViewClinicalHistory) {
      setRevisions([]);
      return;
    }
    let active = true;
    axios.get(`/clinical-revisions/patients/${encodeURIComponent(focusedPatient.id)}`)
      .then(({ data }) => { if (active) setRevisions(data?.revisions || []); })
      .catch(() => { if (active) setRevisions([]); });
    return () => { active = false; };
  }, [focusedPatient?.id, canViewClinicalHistory]);

  useEffect(() => {
    if (!focusedPatient?.id || !canAmendClinicalHistory) {
      setAmendmentContexts([]);
      setAmendmentDentalFindings([]);
      return;
    }
    let active = true;
    axios.get(`/clinical-revisions/patients/${encodeURIComponent(focusedPatient.id)}/amendment-contexts`)
      .then(({ data }) => {
        if (!active) return;
        setAmendmentContexts(data?.contexts || []);
        setAmendmentDentalFindings(data?.dentalFindings || []);
      })
      .catch(() => {
        if (active) {
          setAmendmentContexts([]);
          setAmendmentDentalFindings([]);
        }
      });
    return () => { active = false; };
  }, [focusedPatient?.id, canAmendClinicalHistory]);

  useEffect(() => {
    if (!focusedPatient || activeHistoryTab !== 'all') return;
    const frame = window.requestAnimationFrame(() => {
      focusedPatientRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusedPatientRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeHistoryTab, focusedPatient]);

  useEffect(() => {
    if (canViewBills) {
      fetchCompleted();
      fetchEmiRecords();
    }
  }, [canViewBills]);

  const fetchUpcoming = async () => {
    try {
      const res = await axios.get('/patients/upcoming');
      setUpcomingNew(res.data?.newCases || []);
      setUpcomingFollowup(res.data?.followups || []);
    } catch (err) {
      console.error('Failed to fetch upcoming:', err);
      toast.error(err.response?.data?.error || 'Failed to load upcoming patients');
    }
  };

  const fetchCompleted = async () => {
    try {
      const res = await axios.get('/patients/completed');
      const cases = res.data || [];
      setCompletedCases(cases);
      setRemarksDrafts((current) => {
        const next = { ...current };
        cases.forEach((invoice) => {
          if (next[invoice.id] === undefined) {
            next[invoice.id] = invoice.remarks || '';
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch completed:', err);
      toast.error(err.response?.data?.error || 'Failed to load completed cases');
    }
  };

  const fetchEmiRecords = async () => {
    try {
      const res = await axios.get('/patients/emi-records');
      setEmiRecords(res.data || []);
    } catch (err) {
      console.error('Failed to fetch EMI records:', err);
      toast.error(err.response?.data?.error || 'Failed to load EMI records');
    }
  };

  const fetchAllPatients = async (page = allPatientsPage, query = debouncedAllPatientsSearch) => {
    setAllPatientsLoading(true);
    try {
      const res = await axios.get('/patients/all', {
        params: {
          page,
          limit: allPatientsLimit,
          q: String(query || '').trim(),
        },
      });
      setAllPatients(res.data?.patients || []);
      setAllPatientsTotal(res.data?.total || 0);
      setAllPatientsTotalPages(res.data?.totalPages || 1);
      setAllPatientsPage(res.data?.page || page);
    } catch (err) {
      console.error('Failed to fetch all patients:', err);
      setAllPatients([]);
      setAllPatientsTotal(0);
      setAllPatientsTotalPages(1);
      toast.error(err.response?.data?.error || 'Failed to load patients');
    } finally {
      setAllPatientsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPatients(allPatientsPage, debouncedAllPatientsSearch);
  }, [allPatientsPage, debouncedAllPatientsSearch]);

  useEffect(() => {
    setAllPatientsPage(1);
    setExpandedDirectoryPatient(null);
  }, [debouncedAllPatientsSearch]);

  const runPatientSearch = async (term, options = {}) => {
    const { showErrorToast = false } = options;
    const trimmedTerm = String(term || '').trim();

    if (!trimmedTerm) {
      searchRequestRef.current += 1;
      setSearchResults([]);
      setLoading(false);
      return;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setLoading(true);
    try {
      const res = await axios.get('/patients/search', {
        params: { q: trimmedTerm },
      });
      if (searchRequestRef.current === requestId) {
        setSearchResults(res.data || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
      if (searchRequestRef.current === requestId) {
        setSearchResults([]);
      }
      if (showErrorToast) {
        toast.error('Search failed');
      }
    } finally {
      if (searchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const trimmedTerm = debouncedSearchTerm.trim();
    if (!trimmedTerm) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    runPatientSearch(trimmedTerm);
  }, [debouncedSearchTerm]);

  const handleSearch = async () => {
    await runPatientSearch(searchTerm, { showErrorToast: true });
  };

  const openSearchResult = (patient) => {
    if (!patient?.id) return;
    setFocusedPatient(patient);
    setActiveHistoryTab('all');
    setExpandedDirectoryPatient(patient.id);
  };

  const openClinicalAmendment = (context) => {
    setAmendmentModal(context);
    setAmendmentDraft({ mode: 'ADD', section: 'clinical_update', value: '', reason: '', dentalEntryId: '' });
  };

  const closeClinicalAmendment = () => {
    if (savingAmendment) return;
    setAmendmentModal(null);
  };

  const refreshClinicalAmendmentData = async () => {
    if (!focusedPatient?.id) return;
    const [revisionResponse, contextResponse] = await Promise.all([
      axios.get(`/clinical-revisions/patients/${encodeURIComponent(focusedPatient.id)}`),
      axios.get(`/clinical-revisions/patients/${encodeURIComponent(focusedPatient.id)}/amendment-contexts`),
    ]);
    setRevisions(revisionResponse.data?.revisions || []);
    setAmendmentContexts(contextResponse.data?.contexts || []);
    setAmendmentDentalFindings(contextResponse.data?.dentalFindings || []);
  };

  const saveClinicalAmendment = async (event) => {
    event.preventDefault();
    if (!focusedPatient?.id || !amendmentModal) return;
    setSavingAmendment(true);
    try {
      const selectedDentalFinding = amendmentDentalFindings.find((entry) => String(entry.id) === String(amendmentDraft.dentalEntryId));
      await axios.post(`/clinical-revisions/patients/${encodeURIComponent(focusedPatient.id)}/amendments`, {
        encounterId: amendmentModal.encounterId,
        prescriptionId: amendmentModal.prescriptionId,
        mode: amendmentDraft.mode,
        section: amendmentDraft.section,
        value: amendmentDraft.value,
        reason: amendmentDraft.reason,
        dentalEntryId: amendmentDraft.section === 'dental_findings' && amendmentDraft.mode === 'CORRECT' ? amendmentDraft.dentalEntryId || null : null,
        toothNumber: amendmentDraft.section === 'dental_findings' ? selectedDentalFinding?.toothNumber || null : null,
      });
      await refreshClinicalAmendmentData();
      setRevisionHistoryOpen(true);
      setAmendmentModal(null);
      toast.success(amendmentDraft.mode === 'ADD' ? 'Clinical update added' : 'Clinical information amended');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to save clinical amendment');
    } finally {
      setSavingAmendment(false);
    }
  };

  const resetPatientImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setImportLoading(false);
  };

  const closePatientImport = () => {
    setImportModalOpen(false);
    resetPatientImport();
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);
  };

  const buildImportFormData = () => {
    if (!importFile) {
      throw new Error('Choose a CSV or Excel file first.');
    }

    const formData = new FormData();
    formData.append('file', importFile);
    return formData;
  };

  const previewPatientImport = async () => {
    try {
      setImportLoading(true);
      const res = await axios.post('/patients/import-preview', buildImportFormData(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
      setImportResult(null);
    } catch (err) {
      console.error('Patient import preview failed:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to preview import');
    } finally {
      setImportLoading(false);
    }
  };

  const commitPatientImport = async () => {
    try {
      setImportLoading(true);
      const res = await axios.post('/patients/import-commit', buildImportFormData(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data?.createdCount || 0} patients`);
      await fetchUpcoming();
      await fetchAllPatients(1, debouncedAllPatientsSearch);
      if (searchTerm.trim()) {
        await runPatientSearch(searchTerm, { showErrorToast: false });
      }
    } catch (err) {
      console.error('Patient import commit failed:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to import patients');
    } finally {
      setImportLoading(false);
    }
  };

  const handleRemarkChange = (invoiceId, value) => {
    setRemarksDrafts((current) => ({
      ...current,
      [invoiceId]: value,
    }));
  };

  const saveCompletedRemark = async (invoiceId) => {
    setSavingRemarkId(invoiceId);

    try {
      const res = await axios.put(`/patients/completed/${invoiceId}/remarks`, {
        remarks: remarksDrafts[invoiceId] || '',
      });

      const updatedInvoice = res.data;
      setCompletedCases((current) =>
        current.map((invoice) => (invoice.id === invoiceId ? updatedInvoice : invoice))
      );
      setRemarksDrafts((current) => ({
        ...current,
        [invoiceId]: updatedInvoice.remarks || '',
      }));
      toast.success('Remarks updated');
    } catch (err) {
      console.error('Failed to update remarks:', err);
      toast.error(err.response?.data?.error || 'Failed to update remarks');
    } finally {
      setSavingRemarkId(null);
    }
  };

  const markEmiPaid = async (emiId) => {
    const actionKey = `paid:${emiId}`;
    setEmiActionKey(actionKey);

    try {
      await axios.put(`/patients/emi/${emiId}/pay`);
      toast.success('EMI marked as paid');
      await fetchEmiRecords();
    } catch (err) {
      console.error('Failed to update EMI:', err);
      toast.error(err.response?.data?.error || 'Failed to update EMI');
    } finally {
      setEmiActionKey('');
    }
  };

  const sendEmiReminder = async (emiId) => {
    const actionKey = `remind:${emiId}`;
    setEmiActionKey(actionKey);

    try {
      const res = await axios.post(`/patients/emi/${emiId}/remind`);
      if (res.data?.whatsappUrl) {
        window.open(res.data.whatsappUrl, '_blank', 'noopener,noreferrer');
      }
      toast.success('Reminder prepared');
      await fetchEmiRecords();
    } catch (err) {
      console.error('Failed to send EMI reminder:', err);
      toast.error(err.response?.data?.error || 'Failed to send EMI reminder');
    } finally {
      setEmiActionKey('');
    }
  };

  const fetchDocumentPdfBlob = async (type, id) => {
    const endpoint = type === 'prescription' ? `/prescriptions/${id}/pdf` : `/invoices/${id}/pdf`;
    const response = await axios.get(endpoint, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  };

  const openDocumentPdf = async (type, id) => {
    const actionKey = `${type}:${id}`;
    setDocumentActionKey(actionKey);

    try {
      const pdfBlob = await fetchDocumentPdfBlob(type, id);
      openBlobInNewTab(pdfBlob);
      toast.success(`${type === 'invoice' ? 'Bill' : 'Prescription'} opened`);
    } catch (err) {
      console.error(`Failed to open ${type} PDF:`, err);
      toast.error(err.response?.data?.error || `Failed to open ${type === 'invoice' ? 'bill' : 'prescription'}`);
    } finally {
      setDocumentActionKey('');
    }
  };

  const ensurePatientDocuments = async (record, options = {}) => {
    const documentKey = getPatientDocumentKey(record);
    const patientIdentity = {
      id: getPatientDatabaseId(record),
      code: getPatientCode(record),
    };

    if (!documentKey) return;

    const current = documentsByPatient[documentKey];
    const needsPrescriptions = options.forcePrescriptions || !current?.prescriptionsFetched;
    const needsInvoices = canViewBills && (options.forceInvoices || !current?.invoicesFetched);

    if (!needsPrescriptions && !needsInvoices) {
      return;
    }

    setDocumentsByPatient((prev) => ({
      ...prev,
      [documentKey]: {
        ...(prev[documentKey] || {}),
        loading: true,
        error: '',
      },
    }));

    try {
      const [prescriptionRes, invoiceRes] = await Promise.all([
        needsPrescriptions ? axios.get('/prescriptions') : Promise.resolve(null),
        needsInvoices ? axios.get('/invoices') : Promise.resolve(null),
      ]);

      setDocumentsByPatient((prev) => {
        const previous = prev[documentKey] || {};
        const prescriptions = prescriptionRes
          ? (Array.isArray(prescriptionRes.data) ? prescriptionRes.data : []).filter((item) =>
              matchesPatientDocument(item, patientIdentity)
            )
          : previous.prescriptions || [];
        const invoices = invoiceRes
          ? (Array.isArray(invoiceRes.data) ? invoiceRes.data : []).filter((item) =>
              matchesPatientDocument(item, patientIdentity)
            )
          : previous.invoices || [];

        return {
          ...prev,
          [documentKey]: {
            ...previous,
            loading: false,
            error: '',
            prescriptions,
            prescriptionsFetched: previous.prescriptionsFetched || Boolean(prescriptionRes),
            invoices,
            invoicesFetched: previous.invoicesFetched || Boolean(invoiceRes),
          },
        };
      });
    } catch (err) {
      console.error('Failed to load patient documents:', err);
      setDocumentsByPatient((prev) => ({
        ...prev,
        [documentKey]: {
          ...(prev[documentKey] || {}),
          loading: false,
          error: err.response?.data?.error || 'Failed to load documents',
        },
      }));
    }
  };

  const toggleUpcomingRow = (rowKey, expanded, record) => {
    setExpandedUpcomingRow(expanded ? '' : rowKey);
    if (!expanded) {
      ensurePatientDocuments(record);
    }
  };

  const toggleCompletedCase = (invoice, expanded) => {
    setExpandedCompletedCase(expanded ? null : invoice.id);
    if (!expanded) {
      ensurePatientDocuments(invoice);
    }
  };

  const toggleEmiRecord = (record, expanded) => {
    setExpandedEmiRecord(expanded ? null : record.id);
    if (!expanded) {
      ensurePatientDocuments(record);
    }
  };

  const buildDirectoryPatientRecord = (patient) => ({
    id: `directory-${patient.id}`,
    patientId: patient.id,
    Patient: patient,
  });

  const toggleDirectoryPatient = (patient, expanded) => {
    setExpandedDirectoryPatient(expanded ? null : patient.id);
    if (!expanded) {
      setFocusedPatient(patient);
      ensurePatientDocuments(buildDirectoryPatientRecord(patient));
    }
  };

  const uploadOldRecordForPatient = async (event, patient) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';

    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please choose a JPG or PNG image.');
      return;
    }

    const uploadKey = `old-record:${patient.id}`;
    const patientRecord = buildDirectoryPatientRecord(patient);
    setOldRecordUploadKey(uploadKey);

    try {
      const prescriptionRes = await axios.get(`/prescriptions/by-patient/${patient.id}`);
      const prescriptionId = prescriptionRes.data?.id;

      if (!prescriptionId) {
        throw new Error('Could not prepare a prescription record for this patient.');
      }

      const formData = new FormData();
      formData.append('prescriptionId', prescriptionId);
      formData.append('files', file);

      await axios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Old record attached');
      await ensurePatientDocuments(patientRecord, { forcePrescriptions: true });
    } catch (err) {
      console.error('Old record upload failed:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to attach old record');
    } finally {
      setOldRecordUploadKey('');
    }
  };

  const renderDocumentRow = (type, item) => {
    const isInvoice = type === 'invoice';
    const reference = isInvoice
      ? item.invoiceNumber || `INV-${String(item.id).padStart(5, '0')}`
      : `RX-${String(item.id).padStart(5, '0')}`;
    const summary = isInvoice
      ? `${formatCurrency(item.totalAmount)} | ${formatDateValue(item.date || item.createdAt)}`
      : `${item.status || 'Prescription'} | ${formatDateValue(item.date || item.createdAt)}`;
    const actionKey = `${type}:${item.id}`;

    return (
      <div
        key={`${type}-${item.id}`}
        className="flex flex-col gap-2 rounded-[14px] border border-slate-200/70 bg-slate-50/70 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{reference}</p>
          <p className="mt-1 text-xs font-medium text-slate-500">{summary}</p>
        </div>
        <button
          type="button"
          onClick={() => openDocumentPdf(type, item.id)}
          disabled={documentActionKey === actionKey}
          className={primaryActionChipClasses}
        >
          {documentActionKey === actionKey ? 'Opening...' : 'View'}
        </button>
      </div>
    );
  };

  const renderPatientDocuments = (record) => {
    const documentKey = getPatientDocumentKey(record);
    const state = documentsByPatient[documentKey] || {};
    const prescriptions = state.prescriptions || [];
    const invoices = state.invoices || [];
    const showFinanceNote = !financeStatus.loading && !canViewBills;

    return (
      <div className={`${nestedPanelClasses} overflow-hidden`}>
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <DocumentTextIcon className="h-4 w-4 text-slate-400" />
              Documents
            </h3>
            <p className="mt-1 text-sm text-slate-400">View-only patient PDFs. Printing stays in Print Documents.</p>
          </div>
        </div>

        <div className="grid gap-3.5 p-3.5 lg:grid-cols-2">
          <div className="rounded-[16px] border border-slate-200/70 bg-white p-3.5">
            <h4 className="text-sm font-semibold text-slate-900">Prescriptions</h4>
            <div className="mt-3 space-y-2">
              {state.loading && !state.prescriptionsFetched ? (
                <p className="text-sm text-slate-400">Loading prescriptions...</p>
              ) : prescriptions.length > 0 ? (
                prescriptions.map((item) => renderDocumentRow('prescription', item))
              ) : (
                <p className="rounded-[14px] border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-400">
                  No prescriptions yet.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-[16px] border border-slate-200/70 bg-white p-3.5">
            <h4 className="text-sm font-semibold text-slate-900">Bills</h4>
            <div className="mt-3 space-y-2">
              {financeStatus.loading ? (
                <p className="text-sm text-slate-400">Checking finance access...</p>
              ) : showFinanceNote ? (
                <p className="rounded-[14px] border border-dashed border-amber-200 bg-amber-50 px-3 py-3 text-sm font-medium text-amber-700">
                  Unlock finance to view billing.
                </p>
              ) : state.loading && !state.invoicesFetched ? (
                <p className="text-sm text-slate-400">Loading bills...</p>
              ) : invoices.length > 0 ? (
                invoices.map((item) => renderDocumentRow('invoice', item))
              ) : (
                <p className="rounded-[14px] border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-400">
                  No bills yet.
                </p>
              )}
            </div>
          </div>
        </div>

        {state.error ? (
          <div className="border-t border-slate-100 px-4 py-3 text-sm font-medium text-rose-600">
            {state.error}
          </div>
        ) : null}
      </div>
    );
  };

  const renderPatientHistoryTabs = () => (
    <div className="section-card p-2 shadow-[0_8px_20px_rgba(15,23,42,0.04)] ring-slate-200/60">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {patientHistoryTabs.map((tab) => {
          const active = activeHistoryTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveHistoryTab(tab.key)}
              className={`min-w-max rounded-full border px-4 py-2 text-sm font-semibold transition ${
                active
                  ? 'border-blue-200 bg-blue-600 text-white shadow-[0_6px_16px_rgba(37,99,235,0.22)]'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderFinanceLockedMessage = (message) => (
    <section className={`${sectionSurfaceClasses} bg-slate-50/80`}>
      <div className="px-6 py-14 text-center">
        <p className="text-sm font-semibold text-slate-900">
          {financeStatus.loading ? 'Checking finance access...' : message}
        </p>
        <p className="mt-2 text-sm text-slate-400">
          {financeStatus.loading
            ? 'This section will load automatically if finance is unlocked.'
            : 'Use the existing finance unlock flow to access this section.'}
        </p>
      </div>
    </section>
  );

  const renderAllPatientsDirectory = () => {
    const firstVisible = allPatientsTotal === 0 ? 0 : (allPatientsPage - 1) * allPatientsLimit + 1;
    const lastVisible = Math.min(allPatientsPage * allPatientsLimit, allPatientsTotal);
    const directoryPatients = focusedPatient && !allPatients.some(
      (patient) => Number(patient.id) === Number(focusedPatient.id)
    )
      ? [focusedPatient, ...allPatients]
      : allPatients;

    return (
      <section className={sectionSurfaceClasses}>
        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-50 text-slate-600">
                  <UserIcon className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">All Patients</h2>
                  <p className="mt-1 text-sm text-slate-400">
                    Browse every patient in this clinic, including imported patients with no appointments.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative w-full lg:max-w-sm">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={allPatientsSearch}
                onChange={(event) => setAllPatientsSearch(event.target.value)}
                placeholder="Search all patients..."
                className="input-modern h-10 pl-10 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="mb-3 flex flex-col gap-2 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {allPatientsLoading
                ? 'Loading patients...'
                : `Showing ${firstVisible}-${lastVisible} of ${allPatientsTotal} patients`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAllPatientsPage((page) => Math.max(page - 1, 1))}
                disabled={allPatientsLoading || allPatientsPage <= 1}
                className={neutralActionChipClasses}
              >
                Prev
              </button>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-500">
                Page {allPatientsPage} of {allPatientsTotalPages}
              </span>
              <button
                type="button"
                onClick={() => setAllPatientsPage((page) => Math.min(page + 1, allPatientsTotalPages))}
                disabled={allPatientsLoading || allPatientsPage >= allPatientsTotalPages}
                className={neutralActionChipClasses}
              >
                Next
              </button>
            </div>
          </div>

          {allPatientsLoading && directoryPatients.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-slate-200/70 bg-slate-50/70 px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-900">Loading patient directory...</p>
            </div>
          ) : directoryPatients.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-slate-200/70 bg-slate-50/70 px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-900">No patients yet</p>
              <p className="mt-1 text-sm text-slate-400">
                Imported and newly registered patients will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {directoryPatients.map((patient) => {
                const expanded = expandedDirectoryPatient === patient.id;
                const patientRecord = buildDirectoryPatientRecord(patient);
                const ageGender = [patient.age !== null && patient.age !== undefined ? `${patient.age} yrs` : '', patient.gender]
                  .filter(Boolean)
                  .join(' / ');

                return (
                  <div
                    key={patient.id}
                    ref={Number(patient.id) === Number(focusedPatient?.id) ? focusedPatientRef : null}
                    tabIndex={Number(patient.id) === Number(focusedPatient?.id) ? -1 : undefined}
                    className={`${rowSurfaceClasses} px-4 py-3 focus:outline-none focus:ring-4 focus:ring-blue-100`}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <button
                        type="button"
                        onClick={() => toggleDirectoryPatient(patient, expanded)}
                        className="grid flex-1 grid-cols-1 gap-3 text-left sm:grid-cols-2 xl:grid-cols-5"
                      >
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{patient.name}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Phone</p>
                          <p className="mt-1.5 text-sm text-slate-700">{patient.phone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient ID</p>
                          <p className="mt-1.5 text-sm text-slate-700">{patient.patientId || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Age / Gender</p>
                          <p className="mt-1.5 text-sm text-slate-700">{ageGender || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Added</p>
                          <p className="mt-1.5 text-sm text-slate-700">{formatDateValue(patient.createdAt)}</p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleDirectoryPatient(patient, expanded)}
                        className={toggleButtonClasses}
                      >
                        {expanded ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                        {expanded ? 'Hide' : 'View'}
                        {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                      </button>
                    </div>

                    {expanded ? (
                      <div className="mt-3.5 space-y-3.5 border-t border-slate-100 pt-3.5">
                        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <PhoneIcon className="h-4 w-4 text-slate-400" />
                              {patient.phone || '-'}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <UserIcon className="h-4 w-4 text-slate-400" />
                              {ageGender || 'Age / gender not recorded'}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <MapPinIcon className="h-4 w-4 text-slate-400" />
                              {patient.address || 'No address recorded'}
                            </p>
                          </div>
                        </div>
                        <div className={`${nestedPanelClasses} flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:justify-between`}>
                          <div>
                            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <ArrowUpTrayIcon className="h-4 w-4 text-blue-600" />
                              Attach old record
                            </h3>
                            <p className="mt-1 text-sm text-slate-400">
                              Upload a photo of a previous prescription or clinical record. It will appear in this patient's prescription PDF.
                            </p>
                          </div>
                          <label
                            className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                              oldRecordUploadKey === `old-record:${patient.id}`
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                            }`}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4" />
                            {oldRecordUploadKey === `old-record:${patient.id}` ? 'Uploading...' : 'Attach JPG/PNG'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                              onChange={(event) => uploadOldRecordForPatient(event, patient)}
                              disabled={oldRecordUploadKey === `old-record:${patient.id}`}
                              className="hidden"
                            />
                          </label>
                        </div>
                        <div className={`${nestedPanelClasses} flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:justify-between`}>
                          <div>
                            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <CalendarDaysIcon className="h-4 w-4 text-teal-600" />
                              Patient follow-ups
                            </h3>
                            <p className="mt-1 text-sm text-slate-400">Schedule and review upcoming or overdue follow-up care for this patient.</p>
                          </div>
                          <Link to={`/follow-ups?patientId=${encodeURIComponent(patient.id)}`} className="inline-flex items-center justify-center rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100">
                            Open follow-ups
                          </Link>
                        </div>
                        {renderPatientDocuments(patientRecord)}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderUpcomingColumn = (title, appointments, typeLabel) => (
    <div className={nestedPanelClasses}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-400">
            {appointments.length} {appointments.length === 1 ? 'patient' : 'patients'}
          </p>
        </div>
        <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
          {typeLabel}
        </span>
      </div>

      <div className="p-4">
        {appointments.length === 0 ? (
          <div className="rounded-[16px] border border-dashed border-slate-200/70 bg-slate-50/70 px-5 py-9 text-center">
            <p className="text-sm font-semibold text-slate-900">No cases</p>
            <p className="mt-1 text-sm text-slate-400">Nothing scheduled in this section right now.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {appointments.map((appt) => {
              const rowKey = `${typeLabel}-${appt.id}`;
              const expanded = expandedUpcomingRow === rowKey;

              return (
                <div key={appt.id} className={`${rowSurfaceClasses} px-4 py-3`}>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient</p>
                        <p className="mt-1.5 text-sm font-semibold text-slate-900">{getPatientName(appt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</p>
                        <p className="mt-1.5 text-sm text-slate-700">{formatDateValue(appt.date)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Time</p>
                        <p className="mt-1.5 text-sm text-slate-700">{appt.time || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient ID</p>
                        <p className="mt-1.5 text-sm text-slate-700">{getPatientCode(appt)}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleUpcomingRow(rowKey, expanded, appt)}
                      className={toggleButtonClasses}
                    >
                      {expanded ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                      {expanded ? 'Hide' : 'View'}
                      {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    </button>
                  </div>

                  {expanded ? (
                    <div className="mt-3.5 space-y-3.5 border-t border-slate-100 pt-3.5">
                      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                        <div className={compactMetaCardClasses}>
                          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <PhoneIcon className="h-4 w-4 text-slate-400" />
                            {getPatientPhone(appt)}
                          </p>
                        </div>
                        <div className={compactMetaCardClasses}>
                          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <ClockIcon className="h-4 w-4 text-slate-400" />
                            {formatDateValue(appt.date, 'dd MMM yyyy')} | {appt.time || '-'}
                          </p>
                        </div>
                        <div className={compactMetaCardClasses}>
                          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                            {appt.visitType || typeLabel}
                          </p>
                        </div>
                      </div>
                      {renderPatientDocuments(appt)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="page-title">Patient History</h1>
        <button
          type="button"
          onClick={() => setImportModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
        >
          <DocumentTextIcon className="h-4 w-4" />
          Import Patients
        </button>
      </div>
      {focusedPatient && canViewClinicalHistory ? (
        <section className="rounded-[20px] border border-blue-100 bg-blue-50/50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Clinical record for {focusedPatient.name}</p>
              <p className="mt-1 text-xs text-slate-600">Clinical updates are appended to the selected Encounter. Finalized finance, inventory, and completion state remain unchanged.</p>
            </div>
            <button type="button" className={primaryActionChipClasses} onClick={() => setRevisionHistoryOpen((open) => !open)}>
              {revisionHistoryOpen ? 'Hide Revision History' : `Revision History (${revisions.length})`}
            </button>
          </div>
          {canAmendClinicalHistory ? (
            <div className="mt-4 space-y-2">
              {amendmentContexts.length ? amendmentContexts.map((context) => (
                <div key={context.encounterId} className="flex flex-col gap-3 rounded-xl border border-blue-100 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Encounter {context.encounterId} · {formatDateValue(context.appointment?.date || context.completedAt)}</p>
                    <p className="mt-1 text-xs text-slate-500">{context.appointment?.doctorAssigned || 'Treating doctor'} · {String(context.encounterState || 'Clinical').replace(/_/g, ' ')}</p>
                  </div>
                  <button type="button" className={primaryActionChipClasses} onClick={() => openClinicalAmendment(context)}>Amend / Add Clinical Update</button>
                </div>
              )) : <p className="rounded-xl border border-dashed border-blue-200 bg-white p-3 text-sm text-slate-500">No clinical Encounters are available for amendment.</p>}
            </div>
          ) : null}
          {revisionHistoryOpen ? (
            <div className="mt-4 space-y-2">
              {amendmentContexts.map((context) => {
                const lines = originalContextLines(context, revisions);
                return lines.length ? (
                  <article key={`original-${context.encounterId}`} className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">Original</p>
                    <p className="mt-1 text-xs text-slate-500">{formatDateValue(context.appointment?.date || context.completedAt, 'dd MMM yyyy')} · {context.appointment?.doctorAssigned || 'Treating doctor'}</p>
                    <div className="mt-2 space-y-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs">{lines.map((line) => <p key={line}>{line}</p>)}</div>
                  </article>
                ) : null;
              })}
              {revisions.length ? revisions.map((revision) => (
                <article key={revision.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">{revision.revisionType === 'CLINICAL_UPDATE_ADDED' ? 'Clinical Update Added' : revision.revisionType === 'CLINICAL_INFORMATION_CORRECTED' ? 'Amended' : String(revision.section || '').replace(/_/g, ' ')}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatDateValue(revision.createdAt, 'dd MMM yyyy, hh:mm a')} · {revision.actorNameSnapshot || 'Authorized clinician'} · {String(revision.section || '').replace(/_/g, ' ')}</p>
                  {revision.afterValue?.reasonContext ? <p className="mt-2 text-xs text-slate-500">Reason / Context: {revision.afterValue.reasonContext}</p> : null}
                  {revision.revisionType === 'CLINICAL_UPDATE_ADDED' ? (
                    <div className="mt-2 whitespace-pre-wrap rounded bg-blue-50 p-2 text-xs text-slate-800">{revisionValue(revision.afterValue)}</div>
                  ) : (
                    <div className="mt-2 grid gap-2 sm:grid-cols-2"><div className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs"><span className="font-semibold">Before:</span> {revisionValue(revision.beforeValue) || '—'}</div><div className="whitespace-pre-wrap rounded bg-emerald-50 p-2 text-xs"><span className="font-semibold">After:</span> {revisionValue(revision.afterValue) || '—'}</div></div>
                  )}
                </article>
              )) : <p className="text-sm text-slate-500">No amendments have been recorded for this patient.</p>}
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="section-card p-4 shadow-[0_8px_24px_rgba(15,23,42,0.05)] ring-slate-200/60 md:p-5">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, phone, or patient ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-modern pl-11"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn-primary min-w-[140px]"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {searchResults.length > 0 && (
          <div className="mt-4 table-shell shadow-[0_6px_18px_rgba(15,23,42,0.03)]">
            <div className="mobile-table-scroll">
              <table className="mobile-card-table table-modern">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Phone</th>
                    <th>Patient ID</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((patient) => (
                    <tr
                      key={patient.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openSearchResult(patient)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openSearchResult(patient);
                        }
                      }}
                      className="cursor-pointer transition hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-200"
                      aria-label={`Open patient history for ${patient.name || patient.patientId || patient.id}`}
                    >
                      <td data-label="Patient" className="font-semibold text-slate-900">{patient.name}</td>
                      <td data-label="Phone">{patient.phone}</td>
                      <td data-label="Patient ID">{patient.patientId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && searchTerm.trim() && searchResults.length === 0 && (
          <div className="mt-4 rounded-2xl border border-slate-200/70 bg-slate-50/70 px-4 py-3 text-sm text-slate-400">
            No matching patients found for the current search.
          </div>
        )}
      </div>

      {renderPatientHistoryTabs()}

      {activeHistoryTab === 'upcoming' ? (
      <section className={upcomingSectionClasses}>
        <div className="border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <CalendarDaysIcon className="h-4 w-4" />
            </div>
            <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">Upcoming Patients</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-2">
          {renderUpcomingColumn('New Cases', upcomingNew, 'Consultation')}
          {renderUpcomingColumn('Follow-up Cases', upcomingFollowup, 'Follow-up')}
        </div>
      </section>
      ) : null}

      {activeHistoryTab === 'completed' ? (
        canViewBills ? (
      <section className={completedSectionClasses}>
        <div className="border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
              <BanknotesIcon className="h-4 w-4" />
            </div>
            <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">Case Completed</h2>
          </div>
        </div>

        <div className="p-4">
          {completedCases.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-slate-200/70 bg-slate-50/70 px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-900">No completed cases</p>
              <p className="mt-1 text-sm text-slate-400">Completed billing records will appear here automatically.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {completedCases.map((invoice) => {
                const expanded = expandedCompletedCase === invoice.id;
                const treatments = invoice.InvoiceTreatments || [];
                const remarkValue = remarksDrafts[invoice.id] ?? invoice.remarks ?? '';

                return (
                  <div key={invoice.id} className={`${rowSurfaceClasses} px-4 py-3.5`}>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient Name</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{getPatientName(invoice)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</p>
                          <p className="mt-1.5 text-sm text-slate-700">{formatDateValue(invoice.date)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient ID</p>
                          <p className="mt-1.5 text-sm text-slate-700">{getPatientCode(invoice)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Total Amount Paid</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{formatCurrency(invoice.totalAmount)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Payment Type</p>
                          <p className="mt-1.5 text-sm text-slate-700">{invoice.paymentType || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Remarks</p>
                          <p className="mt-1.5 text-sm text-slate-700">{getRemarkPreview(remarkValue)}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleCompletedCase(invoice, expanded)}
                        className={toggleButtonClasses}
                      >
                        {expanded ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                        {expanded ? 'Hide details' : 'View details'}
                        {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                      </button>
                    </div>

                    {expanded ? (
                      <div className="mt-3.5 space-y-3.5 border-t border-slate-100 pt-3.5">
                        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <PhoneIcon className="h-4 w-4 text-slate-400" />
                              {getPatientPhone(invoice)}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <UserIcon className="h-4 w-4 text-slate-400" />
                              {invoice.Patient?.profession || 'Profession not provided'}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <MapPinIcon className="h-4 w-4 text-slate-400" />
                              {invoice.Patient?.address || 'Address not provided'}
                            </p>
                          </div>
                        </div>

                        <div className={`${nestedPanelClasses} overflow-hidden`}>
                          <div className="border-b border-slate-100 px-4 py-3.5">
                            <h3 className="text-sm font-semibold text-slate-900">Treatment Details</h3>
                          </div>
                          <div className="p-3.5">
                            {treatments.length > 0 ? (
                              <div className="space-y-2">
                                {treatments.map((item) => (
                                  <div
                                    key={item.id}
                                    className="grid grid-cols-1 gap-3 rounded-[16px] border border-slate-200/70 bg-slate-50/70 px-3.5 py-2.5 md:grid-cols-4"
                                  >
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Treatment</p>
                                      <p className="mt-1.5 text-sm font-semibold text-slate-900">{item.Treatment?.name || 'Treatment'}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tooth Number</p>
                                      <p className="mt-1.5 text-sm text-slate-700">{item.toothNumber || '-'}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Quantity</p>
                                      <p className="mt-1.5 text-sm text-slate-700">{item.quantity || 1}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Amount</p>
                                      <p className="mt-1.5 text-sm font-semibold text-slate-900">
                                        {formatCurrency((Number(item.quantity || 1) * Number(item.priceAtTime || 0)))}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">No treatments recorded for this case.</p>
                            )}
                          </div>
                        </div>

                        {renderPatientDocuments(invoice)}

                        <div className={`${nestedPanelClasses} p-3.5`}>
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <ChatBubbleLeftRightIcon className="h-4 w-4 text-slate-400" />
                                Remarks
                              </h3>
                              <p className="mt-1 text-sm text-slate-400">Only remarks remain editable for completed cases.</p>
                            </div>

                            <button
                              type="button"
                              onClick={() => saveCompletedRemark(invoice.id)}
                              disabled={savingRemarkId === invoice.id}
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 shadow-[0_2px_8px_rgba(37,99,235,0.08)] transition hover:bg-blue-100 disabled:opacity-60"
                            >
                              {savingRemarkId === invoice.id ? 'Saving...' : 'Save Remarks'}
                            </button>
                          </div>

                          <textarea
                            value={remarkValue}
                            onChange={(event) => handleRemarkChange(invoice.id, event.target.value)}
                            placeholder="Add remarks for this completed case"
                            className="mt-3.5 min-h-[92px] w-full rounded-[16px] border border-slate-200/70 bg-slate-50/70 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
        ) : (
          renderFinanceLockedMessage('Unlock finance to view completed cases')
        )
      ) : null}

      {activeHistoryTab === 'emi' ? (
        canViewBills ? (
      <section className={emiSectionClasses}>
        <div className="border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
              <CreditCardIcon className="h-4 w-4" />
            </div>
            <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">EMI Records</h2>
          </div>
        </div>

        <div className="p-4">
          {emiRecords.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-slate-200/70 bg-slate-50/70 px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-900">No active EMI cases</p>
              <p className="mt-1 text-sm text-slate-400">EMI payment tracking records will appear here automatically.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {emiRecords.map((record) => {
                const expanded = expandedEmiRecord === record.id;
                const schedule = record.emiSchedule || [];
                const pendingCount = schedule.filter((item) => item.paymentStatus !== 'Done').length;

                return (
                  <div key={record.id} className={`${rowSurfaceClasses} px-4 py-3.5`}>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient Name</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{getPatientName(record)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Start Date</p>
                          <p className="mt-1.5 text-sm text-slate-700">{formatDateValue(record.date)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient ID</p>
                          <p className="mt-1.5 text-sm text-slate-700">{getPatientCode(record)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Total Amount</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{formatCurrency(record.totalAmount)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Down Payment</p>
                          <p className="mt-1.5 text-sm font-semibold text-slate-900">{formatCurrency(record.downPayment)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">EMI Summary</p>
                          <p className="mt-1.5 text-sm text-slate-700">{getEmiSummary(record)}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleEmiRecord(record, expanded)}
                        className={toggleButtonClasses}
                      >
                        {expanded ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                        {expanded ? 'Hide EMI Plan' : 'View EMI Plan'}
                        {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                      </button>
                    </div>

                    {expanded ? (
                      <div className="mt-3.5 space-y-3.5 border-t border-slate-100 pt-3.5">
                        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <PhoneIcon className="h-4 w-4 text-slate-400" />
                              {getPatientPhone(record)}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <UserIcon className="h-4 w-4 text-slate-400" />
                              {record.Patient?.profession || 'Profession not provided'}
                            </p>
                          </div>
                          <div className={compactMetaCardClasses}>
                            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <MapPinIcon className="h-4 w-4 text-slate-400" />
                              {record.Patient?.address || 'Address not provided'}
                            </p>
                          </div>
                        </div>

                        <div className={`${nestedPanelClasses} overflow-hidden`}>
                          <div className="border-b border-slate-100 px-4 py-3.5">
                            <h3 className="text-sm font-semibold text-slate-900">Treatment Details</h3>
                          </div>
                          <div className="p-3.5">
                            {(record.treatments || []).length > 0 ? (
                              <div className="space-y-2">
                                {record.treatments.map((treatment) => (
                                  <div
                                    key={treatment.id}
                                    className="grid grid-cols-1 gap-3 rounded-[16px] border border-slate-200/70 bg-slate-50/70 px-3.5 py-2.5 md:grid-cols-4"
                                  >
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Treatment</p>
                                      <p className="mt-1.5 text-sm font-semibold text-slate-900">{treatment.treatmentName}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tooth Number</p>
                                      <p className="mt-1.5 text-sm text-slate-700">{treatment.toothNumber || '-'}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Quantity</p>
                                      <p className="mt-1.5 text-sm text-slate-700">{treatment.quantity || 1}</p>
                                    </div>
                                    <div>
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Amount</p>
                                      <p className="mt-1.5 text-sm font-semibold text-slate-900">
                                        {formatCurrency((Number(treatment.quantity || 1) * Number(treatment.priceAtTime || 0)))}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">No treatment details available for this EMI case.</p>
                            )}
                          </div>
                        </div>

                        {renderPatientDocuments(record)}

                        <div className={`${nestedPanelClasses} overflow-hidden`}>
                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
                            <div>
                              <h3 className="text-sm font-semibold text-slate-900">EMI Instalment Plan</h3>
                              <p className="mt-1 text-sm text-slate-400">
                                {pendingCount === 0
                                  ? 'All instalments are paid.'
                                  : `${pendingCount} pending instalment${pendingCount === 1 ? '' : 's'} remaining.`}
                              </p>
                            </div>
                            <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
                              {schedule.length} instalments
                            </span>
                          </div>

                          <div className="p-3.5">
                            {schedule.length > 0 ? (
                              <div className="space-y-2">
                                {schedule.map((emi) => {
                                  const paymentDone = emi.paymentStatus === 'Done' || emi.status === 'Paid';
                                  const remindKey = `remind:${emi.id}`;
                                  const paidKey = `paid:${emi.id}`;

                                  return (
                                    <div
                                      key={emi.id}
                                      className="rounded-[16px] border border-slate-200/70 bg-slate-50/70 px-3.5 py-3"
                                    >
                                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                        <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-5">
                                          <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Instalment</p>
                                            <p className="mt-1.5 text-sm font-semibold text-slate-900">#{emi.instalmentNumber}</p>
                                          </div>
                                          <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">EMI Date</p>
                                            <p className="mt-1.5 text-sm text-slate-700">{formatDateValue(emi.dueDate)}</p>
                                          </div>
                                          <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">EMI Amount</p>
                                            <p className="mt-1.5 text-sm font-semibold text-slate-900">{formatCurrency(emi.amount)}</p>
                                          </div>
                                          <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Payment Status</p>
                                            <span
                                              className={`mt-1.5 inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getPaymentStatusClasses(
                                                emi.paymentStatus
                                              )}`}
                                            >
                                              {emi.paymentStatus}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Reminder Stage</p>
                                            <span
                                              className={`mt-1.5 inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getReminderStageClasses(
                                                emi.reminderStage
                                              )}`}
                                            >
                                              {emi.reminderStage?.replace(/_/g, ' ') || 'normal'}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                          {!paymentDone ? (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => sendEmiReminder(emi.id)}
                                                disabled={emiActionKey === remindKey}
                                                className={neutralActionChipClasses}
                                              >
                                                {emiActionKey === remindKey ? 'Preparing...' : (
                                                  <>
                                                    <MessageCircle className="h-4 w-4" />
                                                    Send Reminder
                                                  </>
                                                )}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => markEmiPaid(emi.id)}
                                                disabled={emiActionKey === paidKey}
                                                className={primaryActionChipClasses}
                                              >
                                                {emiActionKey === paidKey ? 'Updating...' : 'Mark Paid'}
                                              </button>
                                            </>
                                          ) : (
                                            <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                                              Fully Paid
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      <div className="mt-2.5 rounded-[14px] border border-slate-200/70 bg-white px-3.5 py-2.5">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Auto-Stamped Remarks</p>
                                        <p className="mt-1.5 text-sm text-slate-700">{emi.lockedRemark || 'No remarks yet'}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">No EMI instalment schedule found.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
        ) : (
          renderFinanceLockedMessage('Unlock finance to view EMI records')
        )
      ) : null}

      {activeHistoryTab === 'all' ? renderAllPatientsDirectory() : null}

      {amendmentModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/55 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="clinical-amendment-title">
          <form onSubmit={saveClinicalAmendment} className="w-full max-w-2xl rounded-[24px] bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <h2 id="clinical-amendment-title" className="text-xl font-semibold text-slate-900">Amend / Add Clinical Update</h2>
                <p className="mt-1 text-sm text-slate-500">Encounter {amendmentModal.encounterId} · {formatDateValue(amendmentModal.appointment?.date || amendmentModal.completedAt)}</p>
              </div>
              <button type="button" onClick={closeClinicalAmendment} aria-label="Close clinical amendment" className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6">
              <div>
                <label htmlFor="amendment-mode" className="block text-sm font-semibold text-slate-800">Change type</label>
                <select id="amendment-mode" value={amendmentDraft.mode} onChange={(event) => setAmendmentDraft((draft) => ({ ...draft, mode: event.target.value, dentalEntryId: '' }))} className="input-modern mt-2">
                  <option value="ADD">Add New Information</option>
                  <option value="CORRECT">Correct Existing Information</option>
                </select>
              </div>
              <div>
                <label htmlFor="amendment-section" className="block text-sm font-semibold text-slate-800">Clinical section</label>
                <select id="amendment-section" value={amendmentDraft.section} onChange={(event) => setAmendmentDraft((draft) => ({ ...draft, section: event.target.value, dentalEntryId: '' }))} className="input-modern mt-2">
                  {amendmentSections.map((section) => <option key={section.key} value={section.key}>{section.label}</option>)}
                </select>
              </div>
              {amendmentDraft.mode === 'CORRECT' && amendmentDraft.section === 'dental_findings' ? (
                <div>
                  <label htmlFor="amendment-dental-finding" className="block text-sm font-semibold text-slate-800">Tooth / existing finding</label>
                  <select id="amendment-dental-finding" required value={amendmentDraft.dentalEntryId} onChange={(event) => setAmendmentDraft((draft) => ({ ...draft, dentalEntryId: event.target.value }))} className="input-modern mt-2">
                    <option value="">Choose a dental finding</option>
                    {amendmentDentalFindings.map((finding) => <option key={finding.id} value={finding.id}>Tooth {finding.toothNumber} · {revisionValue({ issue: finding.issue }) || 'Finding'}</option>)}
                  </select>
                </div>
              ) : null}
              {amendmentDraft.mode === 'CORRECT' ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current information</p>
                  <p className="mt-2 whitespace-pre-wrap">{amendmentDraft.section === 'dental_findings' ? revisionValue({ issue: amendmentDentalFindings.find((entry) => String(entry.id) === String(amendmentDraft.dentalEntryId))?.issue }) || 'Choose a dental finding above.' : amendmentModal.current?.[amendmentDraft.section] || 'No current text is stored for this section; the latest revision will be used when available.'}</p>
                </div>
              ) : null}
              <div>
                <label htmlFor="amendment-value" className="block text-sm font-semibold text-slate-800">{amendmentDraft.mode === 'ADD' ? 'New clinical information' : 'Corrected clinical information'}</label>
                <textarea id="amendment-value" required rows={5} value={amendmentDraft.value} onChange={(event) => setAmendmentDraft((draft) => ({ ...draft, value: event.target.value }))} className="input-modern mt-2" placeholder="Enter the clinical update" />
              </div>
              <div>
                <label htmlFor="amendment-reason" className="block text-sm font-semibold text-slate-800">Reason / Context</label>
                <input id="amendment-reason" required maxLength={500} value={amendmentDraft.reason} onChange={(event) => setAmendmentDraft((draft) => ({ ...draft, reason: event.target.value }))} className="input-modern mt-2" placeholder="e.g. Correction after reviewing radiograph" />
                <p className="mt-1 text-xs text-slate-500">Audit metadata only; it is not added to patient-facing clinical text.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4 sm:px-6">
              <button type="button" onClick={closeClinicalAmendment} disabled={savingAmendment} className={neutralActionChipClasses}>Cancel</button>
              <button type="submit" disabled={savingAmendment} className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{savingAmendment ? 'Saving...' : 'Save Clinical Amendment'}</button>
            </div>
          </form>
        </div>
      ) : null}

      {importModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 px-3 py-6 backdrop-blur-sm sm:items-center">
          <div className="w-full max-w-4xl rounded-[24px] border border-white/70 bg-white shadow-[0_36px_90px_-58px_rgba(15,23,42,0.58)]">
            <div className="relative flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 pr-16 sm:px-6 sm:pr-16">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">Import Patients</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Bring your patient list into Dektal</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-500">
                  Export your patient list from your current software (e.g. Practo) as CSV or Excel, then upload it here.
                  We import patient details: name, phone, age, gender, and address.
                </p>
              </div>
              <button
                type="button"
                onClick={closePatientImport}
                className="absolute right-5 top-4 z-10 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:right-6"
                aria-label="Close import patients"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5 sm:px-6">
              <div className="rounded-[18px] border border-slate-200 bg-slate-50/70 p-4">
                <label className="block text-sm font-semibold text-slate-900" htmlFor="patient-import-file">
                  CSV or Excel file
                </label>
                <input
                  id="patient-import-file"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImportFileChange}
                  className="mt-3 block w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-blue-700"
                />
                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={previewPatientImport}
                    disabled={!importFile || importLoading}
                    className="inline-flex items-center justify-center rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {importLoading && !importPreview ? 'Previewing...' : 'Preview Import'}
                  </button>
                  {importFile ? (
                    <p className="text-sm font-medium text-slate-500">{importFile.name}</p>
                  ) : (
                    <p className="text-sm text-slate-400">Accepted formats: .csv, .xlsx, .xls</p>
                  )}
                </div>
              </div>

              {importPreview ? (
                <div className="rounded-[18px] border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Preview</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {importPreview.importableCount || 0} patients will be imported, {importPreview.skippedCount || 0} skipped.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={commitPatientImport}
                      disabled={importLoading || !importPreview.importableCount}
                      className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {importLoading && importPreview ? 'Importing...' : 'Confirm Import'}
                    </button>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Total rows</p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{importPreview.totalRows || 0}</p>
                    </div>
                    <div className="rounded-[14px] border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Will import</p>
                      <p className="mt-1 text-lg font-semibold text-emerald-700">{importPreview.importableCount || 0}</p>
                    </div>
                    <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600">Skipped</p>
                      <p className="mt-1 text-lg font-semibold text-amber-700">{importPreview.skippedCount || 0}</p>
                    </div>
                  </div>

                  {(importPreview.skipped || []).length > 0 ? (
                    <div className="mt-4 overflow-hidden rounded-[16px] border border-slate-200">
                      <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
                        <h4 className="text-sm font-semibold text-slate-900">Skipped rows</h4>
                      </div>
                      <div className="max-h-72 overflow-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm">
                          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-400">
                            <tr>
                              <th className="px-4 py-2">Row</th>
                              <th className="px-4 py-2">Name</th>
                              <th className="px-4 py-2">Phone</th>
                              <th className="px-4 py-2">Reason</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {importPreview.skipped.map((row, index) => (
                              <tr key={`${row.rowNumber}-${index}`}>
                                <td className="px-4 py-2 font-medium text-slate-700">{row.rowNumber}</td>
                                <td className="px-4 py-2 text-slate-700">{row.name || '-'}</td>
                                <td className="px-4 py-2 text-slate-700">{row.phone || '-'}</td>
                                <td className="px-4 py-2 text-amber-700">{(row.reasons || []).join(', ')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {importResult ? (
                <div className="rounded-[18px] border border-emerald-200 bg-emerald-50 p-4">
                  <h3 className="text-base font-semibold text-emerald-900">Import complete</h3>
                  <p className="mt-1 text-sm font-medium text-emerald-700">
                    Created {importResult.createdCount || 0} patients and skipped {importResult.skippedCount || 0}.
                  </p>
                </div>
              ) : null}

              <div className="flex justify-end border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={closePatientImport}
                  className="inline-flex w-full items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
                >
                  {importResult ? 'Done' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
